import { useCallback, useEffect, useState } from 'react'
import { useWatch, useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'

import { XIcon as CloseIcon } from 'lucide-react'

import Colors from 'colors.json'
import { InputField } from 'components/InputField'
import Dropdown from 'components/Dropdown'
import Button from 'components/Button'
import { DropDownItem } from 'types/components'
import {
  VoucherUpdateFormSchemas,
  VoucherUpdateFormTypes,
} from 'schemas/campaign-voucher-schemas'
import { MemberData } from 'types/member'
import { getMembers } from 'api/members'
import { useVoucherUpdate } from './use-voucher-update'
import {
  useVoucherMerchantsSelectQuery,
  useVoucherMembersSelectQuery,
} from './use-voucher-merchants-lookup'
import { postGetVouchersAPI } from 'api/campaign-voucher'
import { getMerchantAPI } from 'api/merchant'

import { toast } from 'react-toastify'

import { useQueryClient } from 'react-query'
import { QUERY_KEYS } from "constants/querykeys";
import SearchBar from 'components/SearchBar/SearchBar'

import { VOUCHER_TYPES_DROPDOWN, MERCHANT_SELECTION_TYPES_DROPDOWN } from 'constants/filters'
import { MerchantDataTypes } from 'types/merchant'

export const UpdateVoucherForm = ({
  voucherId,
  onCloseModal,
}: {
  voucherId: number
  onCloseModal: () => void
}) => {
  const { selectedMerchants, onSelectMerchants } = useVoucherMerchantsSelectQuery()
  const { selectedMembers, onSelectMembers } = useVoucherMembersSelectQuery()
  const [memberLimitError, setMemberLimitError] = useState<string | null>(null)

  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchVoucher() {
      setLoading(true)
      try {
        const response = await postGetVouchersAPI({
          voucherId,
          // pSize: 1,
          // pNumber: 1,
        })
        const voucher = response.data?.[0]
        if (voucher) {
          const merchantTypeId =
            voucher.merchants?.length === 1 && voucher.merchants[0] === 'All stores'
              ? 1
              : 2
          form.reset({
            voucherId: voucher.voucherId,
            voucherCode: voucher.voucherCode,
            voucherTypeId: voucher.voucherTypeId,
            voucherValue: voucher.voucherValue,
            voucherName: voucher.voucherName ?? '',
            startDate: voucher.startDate?.split('T')[0] ?? '',
            endDate: voucher.endDate?.split('T')[0] ?? '',
            description: voucher.description ?? '',
            imageUrl: voucher.imageUrl ?? '',
            offerValue2: voucher.offerValue2 ?? 0,
            offerValue3: voucher.offerValue3 ?? null,
            offerTypeId: voucher.offerTypeId ?? null,
            pluCode: voucher.pluCode ?? '',
            merchants: voucher.merchants,
            productSkus: voucher.productSkus,
            merchantTypeId,
          })
          if (merchantTypeId === 2 && voucher.merchants?.length) {
            const mapped = voucher.merchants.map((m: string) => {
              const match = m.match(/\((\d+)\)$/)
              const id = match ? match[1] : m
              return { label: m, value: id }
            })

            onSelectMerchants(mapped)
            form.setValue('merchants', mapped.map(m => m.value))
          }
        }
      } finally {
        setLoading(false)
      }
    }
    fetchVoucher()
    // eslint-disable-next-line
  }, [voucherId])


  const form = useForm<VoucherUpdateFormTypes>({
    resolver: zodResolver(VoucherUpdateFormSchemas),
    defaultValues: {
      voucherId: voucherId,
      voucherTypeId: 0,
      voucherValue: 0,
      voucherName: '',
      startDate: null,
      endDate: null,
      description: '',
      imageUrl: '',
      offerValue2: 0,
      offerValue3: null,
      offerTypeId: null,
      pluCode: null,
      merchants: null,
      productSkus: null,
    },
  })

  const merchantTypeId = useWatch({
    control: form.control,
    name: 'merchantTypeId',
  })

  useEffect(() => {
    if (merchantTypeId === 1) {
      // “All stores”
      form.setValue('merchants', ['All stores'])
    } else if (merchantTypeId === 2) {
      // “Specific stores” — remove “All stores” if it exists
      const current = form.getValues('merchants')
      if (Array.isArray(current)) {
        form.setValue(
          'merchants',
          current.filter((m) => m !== 'All stores')
        )
      }
    }
  }, [merchantTypeId, form])

  const {
    mutate: mutateVoucherUpdate,
    isSuccess: isSuccessVoucherUpdate,
    isError: isErrorVoucherUpdate,
    isLoading: isLoadingVoucherUpdate,
  } = useVoucherUpdate()

  const queryClient = useQueryClient();

  const [prevMerchantTypeId, setPrevMerchantTypeId] = useState<number | null>(null)

  useEffect(() => {
    if (prevMerchantTypeId === null && merchantTypeId !== undefined) {
      setPrevMerchantTypeId(merchantTypeId)
      return
    }

    if (prevMerchantTypeId === 2 && merchantTypeId === 1) {
      form.setValue('merchants', ['All stores'], { shouldDirty: true })
    }

    setPrevMerchantTypeId(merchantTypeId ?? null)
  }, [merchantTypeId])

  const onSubmit = useCallback(
    (data: VoucherUpdateFormTypes) => {
      const isMerchantsDirty = form.formState.dirtyFields.merchants;

      const payload = {
        ...data,
        voucherId,
        startDate: data.startDate ? `${data.startDate} 00:00:00` : null,
        endDate: data.endDate ? `${data.endDate} 23:59:59` : null,
        productSkus: data.productSkus?.length ? data.productSkus : null,
        merchants:
          isMerchantsDirty && data.merchantTypeId === 2
            ? data.merchants // array of IDs
            : data.merchantTypeId === 1
              ? ['All Stores']
              : null,
      }

      // console.log('Submitting payload:', payload)

      mutateVoucherUpdate(
        { formData: payload },
        {
          async onSuccess() {
            toast.success('Voucher updated successfully!')
            onCloseModal()

            await queryClient.invalidateQueries({
              predicate: (query) => query.queryKey[0] === QUERY_KEYS.VOUCHER_LIST
            })
          },
          onError: (error) => {
            const message =
              typeof error === 'object' && error !== null
                ? (error as { outcomeUserMessage?: string; outcomeMessage?: string })
                  .outcomeUserMessage ||
                (error as { outcomeMessage?: string }).outcomeMessage ||
                'Failed to update voucher.'
                : String(error)
            toast.error(message)
          },
        }
      )
    },
    [mutateVoucherUpdate, onCloseModal, voucherId, queryClient]
  )

  const [merchantResults, setMerchantResults] = useState<MerchantDataTypes[]>([])
  const [merchantSearchError, setMerchantSearchError] = useState<string | null>(null)
  const [isMerchantSearching, setIsMerchantSearching] = useState(false)

  const [memberResults, setMemberResults] = useState<MemberData[]>([])
  const [memberSearchError, setMemberSearchError] = useState<string | null>(null)
  const [isMemberSearching, setIsMemberSearching] = useState(false)

  const voucherTypeId = form.watch("voucherTypeId")

  useEffect(() => {
    selectedMembers.forEach(onSelectMembers)

    form.setValue("references", [])
    setMemberLimitError(null)
    setMemberResults([])
    setMemberSearchError(null)
  }, [voucherTypeId])

  // const formValues = form.watch()
  // useEffect(() => {
  //   console.log("Current form values:", formValues)
  // }, [formValues])

  if (loading) return <div className="p-4">Loading...</div>

  return (
    <form title="Update Voucher" onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
      <div className="mb-4 flex gap-4">
        <Controller
          control={form.control}
          name="voucherId"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="voucherId"
              label="Voucher ID"
              containerClassName="flex-1"
              value={value ?? ""}
              disable
            />
          )}
        />
        <Controller
          control={form.control}
          name="voucherCode"
          render={({ field: { value } }) => (
            <InputField
              type="input"
              name="voucherCode"
              label="Voucher Code"
              containerClassName="w-full"
              value={value ?? ""}
              disable
            />
          )}
        />
      </div>

      <Controller
        control={form.control}
        name="voucherId"
        render={({ field }) => (
          <input type="hidden" {...field} hidden />
        )}
      />

      <div className="mb-4">
        <Controller
          control={form.control}
          name="voucherTypeId"
          render={({ field: { onChange, value } }) => (
            <Dropdown
              defaultValue={VOUCHER_TYPES_DROPDOWN.find(
                (i) => i.value === String(value)
              )}
              title="Voucher Type"
              options={VOUCHER_TYPES_DROPDOWN}
              onSelect={(option: DropDownItem) => {
                onChange(Number(option.value))
              }}
              className="w-full"
              disabled
            />
          )}
        />
      </div>

      {/* Show Member Lookup when voucher type 2 is selected */}
      {form.watch("voucherTypeId") === 2 ? (
        <div className="mb-4 items-center">
          <p className="mt-4 font-light tracking-wide mb-2">
            Search members to allocate the voucher
          </p>
          <Controller
            name="references"
            control={form.control}
            render={({ field }) => {
              const voucherTypeId = form.watch("voucherTypeId")
              const maxRefs = voucherTypeId === 1 ? 1 : 10
              const canAddMore = (selectedMembers.length || 0) < maxRefs

              return (
                <>
                  <SearchBar
                    isMemberLookup
                    placeholder=""
                    startLoading={() => {
                      setIsMemberSearching(true)
                      setMemberSearchError(null)
                      setMemberResults([])
                    }}
                    stopLoading={() => setIsMemberSearching(false)}
                    startFetch={async (parsedValues) => {
                      try {
                        const members = await getMembers(
                          parsedValues.first_name || parsedValues.fname,
                          parsedValues.last_name || parsedValues.lname,
                          parsedValues.trader_id || parsedValues.id,
                          parsedValues.card_number || parsedValues.card,
                          parsedValues.email || parsedValues.email,
                          parsedValues.phone_number || parsedValues.phoneNumber
                        )
                        if (members.length === 0) {
                          setMemberSearchError("No results found")
                        } else {
                          setMemberResults(members)
                        }
                      } catch (err) {
                        setMemberSearchError("No results found")
                      } finally {
                        setIsMemberSearching(false)
                      }
                    }}
                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                    closeSearchForm={() => { }}
                  />

                  {/* Search results */}
                  {memberSearchError && (
                    <div className="text-red-600 text-sm mt-1">{memberSearchError}</div>
                  )}
                  {isMemberSearching && (
                    <div className="text-black text-sm mt-1">Loading...</div>
                  )}
                  {memberResults.length > 0 && (
                    <div
                      className="overflow-y-auto border rounded mt-2 mb-2"
                      style={{ maxHeight: 240 }}
                    >
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-100 sticky top-0">
                          <tr>
                            <th className="px-2 py-1 text-left">Id</th>
                            <th className="px-2 py-1 text-left">First Name</th>
                            <th className="px-2 py-1 text-left">Last Name</th>
                          </tr>
                        </thead>
                        <tbody>
                          {memberResults.map((m) => {
                            const isSelected = selectedMembers.some(
                              (sel) => sel.value === String(m.traderId)
                            )

                            return (
                              <tr
                                key={m.traderId}
                                className={`hover:bg-gray-50 cursor-pointer ${isSelected ? "bg-blue-100" : ""
                                  }`}
                                onClick={() => {
                                  const newMember: DropDownItem = {
                                    value: String(m.traderId),
                                    label: `${m.traderId} ${m.firstName} ${m.lastName}`,
                                  }

                                  if (isSelected) {
                                    onSelectMembers(newMember)
                                    field.onChange(
                                      (field.value || []).filter(
                                        (id: string) => id !== newMember.value
                                      )
                                    )
                                    setMemberLimitError(null)
                                  } else {
                                    if (!canAddMore) {
                                      setMemberLimitError(
                                        `You can only select up to ${maxRefs} member${maxRefs > 1 ? "s" : ""
                                        } for this voucher type.`
                                      )
                                      return
                                    }

                                    setMemberLimitError(null)
                                    onSelectMembers(newMember)
                                    const updatedRefs =
                                      voucherTypeId === 1
                                        ? [newMember.value]
                                        : [...(field.value || []), newMember.value]

                                    field.onChange(updatedRefs)
                                  }
                                }}
                              >
                                <td className="px-2 py-1">{m.traderId}</td>
                                <td className="px-2 py-1">{m.firstName}</td>
                                <td className="px-2 py-1">{m.lastName}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Selected members */}
                  {selectedMembers.length > 0 && (
                    <div className="mb-4">
                      <p className="font-light tracking-wide">Selected Members</p>
                      <div className="flex gap-2 mt-2 flex-wrap max-w-lg">
                        {selectedMembers.map((item) => (
                          <span
                            key={item.value}
                            onClick={() => {
                              onSelectMembers(item)
                              field.onChange(
                                (field.value || []).filter(
                                  (id: string) => id !== item.value
                                )
                              )
                              setMemberLimitError(null)
                            }}
                            className="cursor-pointer flex items-center justify-between gap-2 bg-gray-100 px-2 py-1 tracking-wide rounded-md"
                          >
                            {item.label}
                            <CloseIcon className="w-3 h-3" />
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Reference limit message */}
                  {memberLimitError && (
                    <div className="text-red text-sm mt-1">{memberLimitError}</div>
                  )}
                </>
              )
            }}
          />
        </div>
      ) : null}

      <div className="mb-4">
        <Controller
          control={form.control}
          name="merchantTypeId"
          render={({ field: { onChange, value } }) => (
            <Dropdown
              value={MERCHANT_SELECTION_TYPES_DROPDOWN.find(
                (i) => i.value === String(value)
              )}
              title="Stores to use the voucher"
              options={MERCHANT_SELECTION_TYPES_DROPDOWN}
              onSelect={(option: DropDownItem) => {
                onChange(Number(option.value))
              }}
              className="w-full"
            />
          )}
        />
      </div>

      {/* Show Merchant Lookup only when type 2 is selected */}
      {form.watch("merchantTypeId") === 2 && (
        <>
          <div className="mb-4 items-center">
            <p className="mt-4 font-light tracking-wide mb-2">
              Search stores to use the voucher
            </p>
            <Controller
              name="merchants"
              control={form.control}
              render={({ field }) => (
                <>
                  <SearchBar
                    isMerchantLookup
                    placeholder="Search stores..."
                    startLoading={() => {
                      setIsMerchantSearching(true)
                      setMerchantSearchError(null)
                      setMerchantResults([])
                    }}
                    stopLoading={() => setIsMerchantSearching(false)}
                    startFetch={async (parsedValues) => {
                      try {
                        const merchants = await getMerchantAPI(
                          parsedValues.traderId || parsedValues.id,
                          parsedValues.merchantName || parsedValues.name,
                          parsedValues.merchantCode || parsedValues.mcode
                        )
                        if (merchants.length === 0) {
                          setMerchantSearchError("No results found")
                        } else {
                          setMerchantResults(merchants)
                        }
                      } catch (err) {
                        setMerchantSearchError("No results found")
                      } finally {
                        setIsMerchantSearching(false)
                      }
                    }}
                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                    closeSearchForm={() => { }}
                  />

                  {/* Search results */}
                  {merchantSearchError && (
                    <div className="text-red-600 text-sm mt-1">{merchantSearchError}</div>
                  )}
                  {isMerchantSearching && (
                    <div className="text-black text-sm mt-1">Loading...</div>
                  )}
                  {merchantResults.length > 0 && (
                    <div
                      className="overflow-y-auto border rounded mt-2 mb-2"
                      style={{ maxHeight: 240 }}
                    >
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-100 sticky top-0">
                          <tr>
                            <th className="px-2 py-1 text-left">Identifier</th>
                            <th className="px-2 py-1 text-left">Merchant Name</th>
                            <th className="px-2 py-1 text-left">Merchant Code</th>
                            <th className="px-2 py-1 text-left">External Id</th>
                          </tr>
                        </thead>
                        <tbody>
                          {merchantResults.map((m) => {
                            const isSelected = selectedMerchants.some(
                              (sel) => sel.value === String(m.traderId)
                            )

                            return (
                              <tr
                                key={m.traderId}
                                className={`hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-blue-100' : ''
                                  }`}
                                onClick={() => {
                                  const newMerchant: DropDownItem = {
                                    value: String(m.traderId),
                                    label: `${m.traderId} ${m.merchantName} (${m.merchantCode})`,
                                  }

                                  if (!isSelected) {
                                    onSelectMerchants(newMerchant)
                                    field.onChange([...(field.value || []), newMerchant.value])
                                  }
                                }}
                              >
                                <td className="px-2 py-1">{m.traderId}</td>
                                <td className="px-2 py-1">{m.merchantName}</td>
                                <td className="px-2 py-1">{m.merchantCode}</td>
                                <td className="px-2 py-1">{m.externalId}</td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Selected merchants */}
                  {selectedMerchants.length > 0 && (
                    <div className="mt-2 mb-4">
                      <p className="font-light tracking-wide">Selected Stores</p>
                      <div className="flex gap-2 mt-2 flex-wrap max-w-lg">
                        {selectedMerchants.map((item) => (
                          <span
                            key={item.value}
                            onClick={() => {
                              onSelectMerchants(item) // toggle remove
                              field.onChange(
                                (field.value || []).filter((id: string) => id !== item.value)
                              )
                            }}
                            className="cursor-pointer flex items-center justify-between gap-2 bg-gray-100 px-2 py-1 tracking-wide rounded-md"
                          >
                            {item.label}
                            <CloseIcon className="w-3 h-3" />
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            />
          </div>
        </>
      )}

      <div className="mb-4">
        <Controller
          control={form.control}
          name="voucherName"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="input"
              name="voucherName"
              label="Voucher Name"
              containerClassName="w-full"
              onChange={onChange}
              value={value ?? ""}
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="description"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="input"
              name="description"
              label="Description"
              containerClassName="w-full"
              onChange={onChange}
              value={value ?? ""}
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="imageUrl"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="input"
              name="imageUrl"
              label="Image URL"
              containerClassName="w-full"
              onChange={onChange}
              value={value ?? ""}
            />
          )}
        />
      </div>
      <div className="mb-4 flex gap-4">
        <Controller
          control={form.control}
          name="offerTypeId"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="input"
              name="offerTypeId"
              label="Offer Type ID"
              containerClassName="flex-1"
              value={value ?? ""}
              onChange={(e) => onChange(e.target.value ? Number(e.target.value) : 0)}
            />
          )}
        />

        <Controller
          control={form.control}
          name="offerValue2"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="input"
              name="offerValue2"
              label="Min Spend Amount"
              containerClassName="flex-1"
              value={value ?? ""}
              onChange={(e) => onChange(e.target.value ? Number(e.target.value) : 0)}
            />
          )}
        />

        <Controller
          control={form.control}
          name="offerValue3"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="number"
              name="offerValue3"
              label="Offer Value"
              containerClassName="flex-1"
              value={value ?? ""}
              onChange={(e) => onChange(e.target.value ? Number(e.target.value) : 0)}
            />
          )}
        />
      </div>
      <div className="mb-4 flex gap-4">
        <Controller
          control={form.control}
          name="voucherValue"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="input"
              name="voucherValue"
              label="Voucher Value"
              containerClassName="flex-1"
              value={value ?? ""}
              onChange={(e) => onChange(e.target.value ? Number(e.target.value) : 0)}
            />
          )}
        />

        <Controller
          control={form.control}
          name="pluCode"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="input"
              name="pluCode"
              label="PLU Code"
              containerClassName="w-full"
              onChange={onChange}
              value={value ?? ""}
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="productSkus"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="input"
              name="productSkus"
              label="Product SKUs (comma separated)"
              containerClassName="w-full"
              value={(value || []).join(", ")}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const arr = e.target.value
                  .split(",")
                  .map((sku) => sku.trim())
                  .filter((sku) => sku.length > 0)
                onChange(arr)
              }}
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="startDate"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="date"
              name="startDate"
              label="Start Date"
              containerClassName="w-full"
              onChange={onChange}
              value={value ?? ""}
            />
          )}
        />
      </div>
      <div className="mb-4">
        <Controller
          control={form.control}
          name="endDate"
          render={({ field: { onChange, value } }) => (
            <InputField
              type="date"
              name="endDate"
              label="End Date"
              containerClassName="w-full"
              onChange={onChange}
              value={value ?? ""}
            />
          )}
        />
      </div>
      <div className="flex justify-end pb-4 pl-4 pr-4">
        <Button
          type="submit"
          className="mr-2 rounded bg-primary"
          disabled={isLoadingVoucherUpdate}
        >
          <span className="tracking-wider font-bold">
            {isLoadingVoucherUpdate ? 'Submitting ...' : 'Submit'}
          </span>
        </Button>
        <Button
          className="bg-white border rounded border-primary"
          onClick={onCloseModal}
          style={{ color: Colors.primary }}
        >
          <span className="tracking-wider font-bold">Cancel</span>
        </Button>
      </div>
    </form >
  )
}
